import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Layout from './components/Layout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Applications from './pages/Applications';
import Users from './pages/Users';
import Managers from './pages/Managers';
import Mailings from './pages/Mailings';
import PromptsContentPage from './pages/Prompts';
import Feedback from './pages/Feedback';
import Communications from './pages/Communications';
import Dialogs from './pages/Dialogs';
import ResetPassword from './pages/ResetPassword';
import { getDefaultRedirectPath } from './utils/redirectByRole';
import { canAccessPath, getAccessDeniedRedirect } from './utils/roleAccess';
import './App.css';

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return user ? children : <Navigate to="/login" />;
}

function RoleProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  if (!canAccessPath(user.role, location.pathname)) {
    return <Navigate to={getAccessDeniedRedirect(user.role)} replace />;
  }

  return children;
}

function AppLayoutRoute({ children }) {
  return (
    <RoleProtectedRoute>
      <Layout>{children}</Layout>
    </RoleProtectedRoute>
  );
}

function RootRedirect() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  const redirectPath = getDefaultRedirectPath(user?.role);
  return <Navigate to={redirectPath} />;
}

function AppRoutes() {
  const { user } = useAuth();

  return (
    <Routes>
      <Route
        path="/login"
        element={user ? <Navigate to="/" /> : <Login />}
      />
      <Route path="/reset-password" element={<ResetPassword />} />
      {/* <Route path="/" element={
        <ProtectedRoute>
          <Layout>
            <Dashboard />
          </Layout>
        </ProtectedRoute>
      } /> */}
      <Route path="/" element={<RootRedirect />} />
      <Route path="/applications" element={
        <AppLayoutRoute>
          <Applications />
        </AppLayoutRoute>
      } />
      <Route path="/users" element={
        <AppLayoutRoute>
          <Users />
        </AppLayoutRoute>
      } />
      <Route path="/managers" element={
        <AppLayoutRoute>
          <Managers />
        </AppLayoutRoute>
      } />
      <Route path="/prompts" element={
        <AppLayoutRoute>
          <PromptsContentPage />
        </AppLayoutRoute>
      } />
      {/* <Route path="/feedback" element={
        <ProtectedRoute>
          <Layout>
            <Feedback />
          </Layout>
        </ProtectedRoute>
      } /> */}
      <Route path="/communications" element={
        <AppLayoutRoute>
          <Communications />
        </AppLayoutRoute>
      } />
      <Route path="/dialogs" element={
        <AppLayoutRoute>
          <Dialogs />
        </AppLayoutRoute>
      } />
      <Route path="/mailings" element={
        <AppLayoutRoute>
          <Mailings />
        </AppLayoutRoute>
      } />
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <div className="App">
          <AppRoutes />
          <Toaster 
            position="top-right"
            toastOptions={{
              duration: 4000,
              style: {
                background: '#363636',
                color: '#fff',
              },
            }}
          />
        </div>
      </Router>
    </AuthProvider>
  );
}

export default App;